import { useState, useEffect, useCallback } from 'react'
import { apiClient } from '../services/api'
import { useTranslation } from 'react-i18next'
import { useToast } from '../components/shared/Toast'
import { Modal } from '../components/shared/Modal'

interface Contract {
  rowKey: string
  userId: string
  parkingSpaceId: string
  parkingSpaceNumber?: string
  apartmentNumber: string
  parentContractId: string | null
  status: string
  startDate: string
  endDate: string | null
}

interface ContractsResponse {
  contracts: Contract[]
  subUsers: Record<string, unknown[]>
}

export function ContractsPage() {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const [data, setData] = useState<ContractsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [ending, setEnding] = useState<string | null>(null)
  const [showRequestContract, setShowRequestContract] = useState(false)
  const [requestForm, setRequestForm] = useState({ parkingSpaceNumber: '' })
  const [submitting, setSubmitting] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    apiClient.get<ContractsResponse>('/api/user/contracts')
      .then(r => setData(r))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  async function endContract(contractId: string) {
    // Calculate what end date will be (last day of next month) for the confirm message
    const now = new Date()
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 2, 0)
    const endDate = nextMonth.toLocaleDateString('fi-FI')

    if (!window.confirm(t('contracts.endConfirm', { endDate }))) return

    setEnding(contractId)
    try {
      await apiClient.post(`/api/user/contracts/${contractId}/end`)
      showToast(t('contracts.ended_success'), 'success')
      load()
    } catch {
      showToast(t('errors.generic'), 'error')
    } finally {
      setEnding(null)
    }
  }

  async function requestContract() {
    setSubmitting(true)
    try {
      await apiClient.post('/api/user/contracts/request', requestForm)
      showToast(t('contracts.requestSent'), 'success')
      setShowRequestContract(false)
      setRequestForm({ parkingSpaceNumber: '' })
    } catch {
      showToast(t('errors.generic'), 'error')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <p className="text-brand-500 py-8 text-center">{t('contracts.loading')}</p>
  if (!data || data.contracts.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-brand-700">{t('contracts.title')}</h1>
          <button
            onClick={() => {
              setShowRequestContract(true)
              setRequestForm({ parkingSpaceNumber: '' })
            }}
            className="bg-brand-500 text-white text-sm px-4 py-2 rounded-lg hover:bg-brand-600 transition-colors"
          >
            {t('contracts.requestNew')}
          </button>
        </div>
        <p className="text-brand-400">{t('contracts.noContracts')}</p>

        {/* Request Contract Modal */}
        <Modal
          open={showRequestContract}
          onClose={() => setShowRequestContract(false)}
          title={t('contracts.requestNew')}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-brand-700 mb-1">{t('contracts.requestParking')}</label>
              <input
                type="text"
                value={requestForm.parkingSpaceNumber}
                onChange={e => setRequestForm(f => ({ ...f, parkingSpaceNumber: e.target.value }))}
                className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={requestContract}
                disabled={submitting || !requestForm.parkingSpaceNumber}
                className="bg-brand-500 text-white text-sm px-5 py-2 rounded-lg hover:bg-brand-600 disabled:opacity-50 transition-colors"
              >
                {t('contracts.requestNew')}
              </button>
              <button
                onClick={() => setShowRequestContract(false)}
                className="text-brand-500 text-sm px-5 py-2 rounded-lg hover:bg-brand-50 transition-colors"
              >
                {t('admin.users.cancel')}
              </button>
            </div>
          </div>
        </Modal>
      </div>
    )
  }

  const hasActivePrimary = data.contracts.some(c => !c.parentContractId && c.status === 'active')
  const buttonLabel = hasActivePrimary ? t('contracts.requestNew') : t('contracts.requestPrimary')

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-brand-700">{t('contracts.title')}</h1>
      </div>
      <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm px-4 py-3 rounded-lg">
        {hasActivePrimary ? t('contracts.description') : t('contracts.noPrimaryInfo')}
      </div>
      <div className="flex items-center justify-end">
        <button
          onClick={() => {
            setShowRequestContract(true)
            setRequestForm({ parkingSpaceNumber: '' })
          }}
          className="bg-brand-500 text-white text-sm px-4 py-2 rounded-lg hover:bg-brand-600 transition-colors"
        >
          {buttonLabel}
        </button>
      </div>

      <div className="space-y-4">
        {data.contracts.map(c => {
          const isPrimary = !c.parentContractId
          const isActive = c.status === 'active'
          const isPending = c.status === 'pending'

          return (
            <div key={c.rowKey} className={`rounded-xl shadow-sm overflow-hidden ${isPending ? 'bg-amber-50 border border-amber-200' : 'bg-white'}`}>
              <div className="px-4 py-3">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-brand-700">
                      {isPrimary ? t('contracts.primary') : t('contracts.sub')}
                      {isPending && <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">{t('contracts.pendingApproval')}</span>}
                    </p>
                    <p className="text-xs text-brand-500 mt-1">
                      {t('contracts.parking')}: {c.parkingSpaceNumber || '-'}
                      {' · '}{t('contracts.requestApartment')}: {c.apartmentNumber}
                    </p>
                    {c.startDate && (
                      <p className="text-xs text-brand-400 mt-0.5">
                        {t('contracts.startDate')}: {c.startDate}
                        {c.endDate && ` · ${t('contracts.endDate')}: ${c.endDate}`}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      isPending ? 'bg-amber-100 text-amber-700' :
                      isActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {isActive ? t('contracts.active') : t('contracts.ended')}
                    </span>
                    {isActive && (
                      <button
                        onClick={() => endContract(c.rowKey)}
                        disabled={ending === c.rowKey}
                        className="text-red-500 text-xs px-3 py-1 rounded hover:bg-red-50 hover:text-red-700 disabled:opacity-50 transition-colors"
                      >
                        {t('contracts.end')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Request Contract Modal */}
      <Modal
        open={showRequestContract}
        onClose={() => setShowRequestContract(false)}
        title={buttonLabel}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-brand-700 mb-1">{t('contracts.requestParking')}</label>
            <input
              type="text"
              value={requestForm.parkingSpaceNumber}
              onChange={e => setRequestForm(f => ({ ...f, parkingSpaceNumber: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              onClick={requestContract}
              disabled={submitting || !requestForm.parkingSpaceNumber}
              className="bg-brand-500 text-white text-sm px-5 py-2 rounded-lg hover:bg-brand-600 disabled:opacity-50 transition-colors"
            >
              {buttonLabel}
            </button>
            <button
              onClick={() => setShowRequestContract(false)}
              className="text-brand-500 text-sm px-5 py-2 rounded-lg hover:bg-brand-50 transition-colors"
            >
              {t('admin.users.cancel')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
